import type {ReactNode} from 'react';
import {cn} from '../lib/cn';

export type ThreeDotsLoaderProps = {
  height?: number;
  width?: number;
  color?: string;
  label?: string | ReactNode;
  className?: string;
  labelPlacement?: 'right' | 'left';
};

function Dots({
  height,
  width,
  color,
}: Required<Pick<ThreeDotsLoaderProps, 'height' | 'width' | 'color'>>) {
  return (
    <span
      className="inline-flex items-center justify-center gap-[15%]"
      style={{height, width}}
      aria-hidden="true"
    >
      <span
        className="h-[22%] w-[22%] animate-chekin-three-dots rounded-full [animation-delay:-0.32s]"
        style={{backgroundColor: color}}
      />
      <span
        className="h-[22%] w-[22%] animate-chekin-three-dots rounded-full [animation-delay:-0.16s]"
        style={{backgroundColor: color}}
      />
      <span
        className="h-[22%] w-[22%] animate-chekin-three-dots rounded-full"
        style={{backgroundColor: color}}
      />
    </span>
  );
}

export function ThreeDotsLoader({
  height = 30,
  width = 30,
  color = '#E3E3E3FF',
  label = '',
  className,
  labelPlacement = 'right',
}: ThreeDotsLoaderProps) {
  const dots = <Dots color={color} height={height} width={width} />;

  if (label) {
    return (
      <div
        className={cn(
          'flex items-center justify-center gap-x-[13px] [&>div]:text-sm [&>div]:font-bold [&>div]:uppercase [&>div]:text-[var(--chekin-neutral-500)] [&>div]:opacity-50',
          className,
        )}
        role="progressbar"
      >
        {labelPlacement === 'right' ? (
          <>
            {dots}
            <div>{label}</div>
          </>
        ) : (
          <>
            <div>{label}</div>
            {dots}
          </>
        )}
      </div>
    );
  }

  return (
    <div role="progressbar" className={className}>
      {dots}
    </div>
  );
}
